
import React from 'react';
import type { ApiLogEntry } from '../types';
import { useLocalization } from '../hooks/useLocalization';

interface ApiLogViewerProps {
    logs: ApiLogEntry[];
    isOpen: boolean;
    onClose: () => void;
}

const ChevronDownIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
);

const TerminalIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

export const ApiLogViewer: React.FC<ApiLogViewerProps> = ({ logs, isOpen, onClose }) => {
    const { t } = useLocalization();

    const formatRequest = (request: any) => {
        if (typeof request === 'string') return request;
        return JSON.stringify(request, null, 2);
    }

    if (!isOpen) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[60] flex flex-col justify-end pointer-events-none">
            {/* Backdrop to close when clicking outside */}
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto" onClick={onClose}></div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pointer-events-auto relative z-10">
                <div className="bg-gray-800 text-white rounded-t-lg shadow-2xl border-t border-gray-700">
                    <button
                        className="w-full flex justify-between items-center p-3 text-left font-semibold bg-gray-900 rounded-t-lg hover:bg-gray-700 transition-colors"
                        onClick={onClose}
                    >
                        <div className="flex items-center gap-2">
                            <TerminalIcon />
                            <span>{t('api_logs')}</span>
                            <span className="text-xs bg-gray-700 px-2 py-0.5 rounded-full text-gray-300">{logs.length}</span>
                        </div>
                        <ChevronDownIcon />
                    </button>
                    
                    <div className="p-4 border-t border-gray-600 max-h-96 overflow-y-auto bg-gray-800">
                        {logs.length === 0 ? (
                            <p className="text-gray-400 text-sm text-center py-4">No API calls have been made yet.</p>
                        ) : (
                            logs.map((log, index) => (
                                <details key={index} className="mb-3 bg-gray-900/50 rounded-md border border-gray-700">
                                    <summary className="p-3 cursor-pointer flex justify-between items-center hover:bg-gray-700/50 transition-colors rounded-md">
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${log.status === 'success' ? 'bg-green-900/50 text-green-400 border border-green-800' : 'bg-red-900/50 text-red-400 border border-red-800'}`}>
                                                {log.status}
                                            </span>
                                            <span className="font-mono text-xs text-amber-500 truncate">{log.endpoint}</span>
                                        </div>
                                        <span className="font-mono text-[10px] text-gray-500 flex-shrink-0 ml-2">{new Date(log.timestamp).toLocaleTimeString()}</span>
                                    </summary>
                                    <div className="p-3 border-t border-gray-700 bg-black/20 text-xs font-mono space-y-3">
                                        <div>
                                            <span className="text-gray-500 block mb-1 font-bold">{t('request')}:</span>
                                            <pre className="text-green-300 whitespace-pre-wrap break-all pl-2 border-l-2 border-green-900">
                                                {formatRequest(log.request)}
                                            </pre>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1 font-bold">{t('response')}:</span>
                                            <pre className="text-blue-300 whitespace-pre-wrap break-all pl-2 border-l-2 border-blue-900 max-h-40 overflow-y-auto">
                                                {log.response}
                                            </pre>
                                        </div>
                                    </div>
                                </details>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
